/**
 * Configuração centralizada de features e planos.
 * Fonte única de verdade para gating de funcionalidades.
 *
 * O que decide acesso mora AQUI e não muda com o idioma: `featureKey` casa com
 * o array `features` do plano no banco, `minPlan` casa com o nome do plano no
 * banco e no Stripe, e o id do módulo em `MODULE_FEATURE_MAP` é o mesmo do
 * roteamento. Traduzir qualquer um deles quebraria o gate.
 *
 * O que aparece na tela (nome e descrição da feature) mora no dicionário, em
 * `account.features`, indexado pelo MESMO `featureKey`. Aqui os campos
 * `displayName` e `description` são GETTERS que leem o idioma corrente: a forma
 * do objeto continua a mesma, então Sidebar, ModuleTabBar e a Wiki, que os leem
 * direto, ficam bilíngues sem mudar uma linha. O tipo continua derivando do
 * português, que é a fonte da forma.
 */

import ptAccount from '../i18n/locales/pt/account';
import { getCurrentCopy } from '../i18n/current';

type PlanName = 'free' | 'starter' | 'professional' | 'business';

type FeatureId = keyof typeof ptAccount.features;

interface FeatureConfigEntry {
  displayName: string;
  description: string;
  minPlan: PlanName;
  featureKey: string;
}

interface PlanHierarchyEntry {
  order: number;
  displayName: string;
}

/** Monta a entrada juntando o gate (aqui) com a cópia (dicionário). */
const entry = (id: FeatureId, minPlan: PlanName): FeatureConfigEntry => ({
  get displayName() { return getCurrentCopy().account.features[id].name; },
  get description() { return getCurrentCopy().account.features[id].description; },
  minPlan,
  featureKey: id,
});

// Metadata de cada feature
export const FEATURE_CONFIG: Record<string, FeatureConfigEntry> = {
  // Módulos
  gantt_basic: entry('gantt_basic', 'free'),
  calendar: entry('calendar', 'free'),
  timeline: entry('timeline', 'starter'),
  todos: entry('todos', 'starter'),
  risks: entry('risks', 'professional'),
  changes: entry('changes', 'professional'),
  costs: entry('costs', 'professional'),
  raci_matrix: entry('raci_matrix', 'starter'),
  eap_wbs: entry('eap_wbs', 'starter'),
  // Gantt
  gantt_dependencies: entry('gantt_dependencies', 'starter'),
  // Export
  export_csv_limited: entry('export_csv_limited', 'free'),
  export_csv: entry('export_csv', 'starter'),
  // Excel entra no starter, junto do CSV, e não no professional junto do PDF.
  // A razão é de ativação, não de generosidade: a planilha é o formato que o PMO leva para
  // a diretoria, e prender o formato mais compartilhado no plano mais caro tira o produto
  // justamente da reunião onde ele seria visto. O starter já é pago, então continua sendo
  // conversão.
  export_excel: entry('export_excel', 'starter'),
  export_pdf: entry('export_pdf', 'professional'),
  export_png: entry('export_png', 'professional'),
  // Compartilhamento
  // O link público é a porta de entrada de quem ainda não tem conta: abre,
  // vê o cronograma e não precisa se cadastrar. Cobrar por ele era paywall
  // em cima do próprio canal de distribuição, então ele é free.
  // O que continua pago é o que se faz depois (editar, membros, senha).
  share_public: entry('share_public', 'free'),
  share_password: entry('share_password', 'professional'),
  // API
  api_access: entry('api_access', 'professional'),
  // Portfólio
  portfolio: entry('portfolio', 'professional'),
  // Capacidade e Alocação
  capacity_planning: entry('capacity_planning', 'professional'),
  ai_project_creation: entry('ai_project_creation', 'starter'),
  comments: entry('comments', 'starter'),
  organizations: entry('organizations', 'starter'),
  import_external: entry('import_external', 'starter'),
  multi_assignee: entry('multi_assignee', 'starter'),
  // Templates, Baseline e Caminho Crítico
  project_templates: entry('project_templates', 'starter'),
  schedule_baseline: entry('schedule_baseline', 'starter'),
  critical_path: entry('critical_path', 'professional'),
  // Workload e Alertas de Capacidade
  team_workload: entry('team_workload', 'starter'),
  capacity_alerts: entry('capacity_alerts', 'starter'),
  // Suporte
  priority_support: entry('priority_support', 'professional')
};

// Mapeia IDs dos módulos do Sidebar para feature keys
export const MODULE_FEATURE_MAP: Record<string, string | null> = {
  gantt: null,        // Sempre disponível (gantt_basic é universal)
  kanban: null,       // Sempre disponível (plano Free)
  team: null,         // Sempre disponível
  schedule: 'timeline',
  calendar: null,     // Sempre disponível
  todos: 'todos',
  raci: 'raci_matrix',
  eap: 'eap_wbs',
  risks: 'risks',
  changes: 'changes',
  costs: 'costs',
  portfolio: 'portfolio',
  wiki: null            // Sempre disponível
};

/**
 * Hierarquia e display dos planos. O `displayName` é o nome comercial que o
 * banco e o Stripe usam ("Free", "Starter"...): é identificador, não rótulo, e
 * por isso não passa pelo dicionário.
 */
export const PLAN_HIERARCHY: Record<PlanName, PlanHierarchyEntry> = {
  free: { order: 0, displayName: 'Free' },
  starter: { order: 1, displayName: 'Starter' },
  professional: { order: 2, displayName: 'Professional' },
  business: { order: 3, displayName: 'Business' }
};

/**
 * Retorna o nome do plano mínimo necessário para uma feature
 */
export function getMinPlanForFeature(featureKey: string): PlanName {
  const config = FEATURE_CONFIG[featureKey];
  if (!config) return 'free';
  return config.minPlan;
}

/**
 * Retorna o display name do plano mínimo para uma feature
 */
export function getMinPlanDisplayName(featureKey: string): string {
  const minPlan = getMinPlanForFeature(featureKey);
  return PLAN_HIERARCHY[minPlan]?.displayName || 'Free';
}

/**
 * Verifica se um plano atende ao requisito mínimo de uma feature.
 * Usa a hierarquia local (FEATURE_CONFIG) em vez de depender do array 'features' no banco.
 */
export function canUsePlanFeature(userPlanName: string, featureKey: string): boolean {
  const minPlan = getMinPlanForFeature(featureKey);
  const userOrder = PLAN_HIERARCHY[userPlanName as PlanName]?.order ?? 0;
  const minOrder = PLAN_HIERARCHY[minPlan]?.order ?? 0;
  return userOrder >= minOrder;
}
